import { GeoJsonProperties, Geometry, GeometryObject } from 'geojson';

import { GeoJsonPointFeatureSource } from '../models';

/**
 * Typescript guard function to know
 * if we deal with GeoJSON FeatureCollection or Feature.
 */
export function isGeoJSONFeatureCollection(
  json: GeoJSON.FeatureCollection | GeoJSON.Feature
): json is GeoJSON.FeatureCollection {
  return (<GeoJSON.FeatureCollection>json).type === 'FeatureCollection';
}

/**
 * Get GeoJSON.Point object.
 * @param lngLat Two number tuple following the [longitude, latitude] format.
 */
export const getGeoJSONPointGeometry = (lngLat: [number, number]): GeoJSON.Point => {
  return {
    type: 'Point',
    coordinates: [lngLat[0], lngLat[1]],
  };
};

/**
 * Get a GeoJSON.Feature.
 * Note: id here is required to make filtering
 * easier down the road.
 */
export const getGeoJSONFeature = <G extends GeometryObject = Geometry, P = GeoJsonProperties>(source: {
  id: string | number;
  geometry: G;
  properties: P;
}): GeoJSON.Feature<G, P> => {
  return {
    type: 'Feature',
    geometry: source.geometry,
    id: source.id,
    properties: source.properties,
  };
};

/**
 * Get a GeoJSON.FeatureCollection.
 */
export const getGeoJSONFeatureCollection = <G extends GeometryObject = Geometry, P = GeoJsonProperties>(
  features: GeoJSON.Feature<G, P>[]
): GeoJSON.FeatureCollection<G, P> => {
  return {
    type: 'FeatureCollection',
    features: features,
  };
};

/**
 * Helper function to create a GeoJSON.Feature with a GeoJSON.Point geometry object.
 */
export const getGeoJSONPointFeature = <P = GeoJsonProperties>(
  source: GeoJsonPointFeatureSource<P>
): GeoJSON.Feature<GeoJSON.Point, P> => {
  return getGeoJSONFeature<GeoJSON.Point, P>({
    id: source.id,
    geometry: getGeoJSONPointGeometry([source.longitude, source.latitude]),
    properties: source.properties,
  });
};
